import React, { useState } from "react";

/**
 * WelcomeBanner — one-time dismissible banner on the workspace home
 * that orients first-time visitors. Persists dismissal in localStorage
 * under `workspace.welcome-seen` so it disappears for return visits.
 *
 * When `onStartTour` is provided it also offers a "Take a tour" CTA that
 * launches the guided Joyride walkthrough. Taking the tour or dismissing
 * both mark the banner seen, so a returning user is never shown the banner
 * and the tour redundantly. `forceShow` re-triggers it from the account menu.
 *
 * Server-renders nothing; only mounts when `window.localStorage` is
 * available and the key isn't set.
 */

const STORAGE_KEY = "workspace.welcome-seen";

const hasBeenDismissed = () => {
  if (typeof window === "undefined") return true;
  try { return window.localStorage.getItem(STORAGE_KEY) === "true"; } catch (_) { return false; }
};

const WelcomeBanner = ({ onDismiss = null, onStartTour = null, forceShow = false }) => {
  const [dismissed, setDismissed] = useState(() => (forceShow ? false : hasBeenDismissed()));

  const markSeen = () => {
    setDismissed(true);
    try { window.localStorage.setItem(STORAGE_KEY, "true"); } catch (_) { /* ignore */ }
  };

  const dismiss = () => {
    markSeen();
    onDismiss?.();
  };

  // Launching the tour also retires the banner — the walkthrough covers the
  // same ground, so we don't surface both on the next visit.
  const startTour = () => {
    markSeen();
    onStartTour?.();
  };

  if (dismissed) return null;

  return (
    <div className="welcome" role="status" data-testid="v2-welcome">
      <div className="welcome__icon" aria-hidden="true">👋</div>
      <div className="welcome__body">
        <div className="welcome__title">Welcome to Plex Co-Scientist</div>
        <div className="welcome__sub">
          Pick a <b>program</b> to see its projects. Each project is a folder of <b>chats</b>;
          ask anything and Plex routes specialists, queries datasets, and streams a single-voice answer.
          Press <kbd>⌘K</kbd> from anywhere to jump.
        </div>
        {onStartTour && (
          <div className="welcome__actions">
            <button
              type="button"
              className="welcome__tour"
              onClick={startTour}
              data-testid="v2-welcome-take-tour"
            >
              Take a tour
            </button>
            <button
              type="button"
              className="welcome__later"
              onClick={dismiss}
              data-testid="v2-welcome-maybe-later"
            >
              Maybe later
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        className="welcome__close"
        onClick={dismiss}
        aria-label="Dismiss welcome banner"
        data-testid="v2-welcome-dismiss"
      >
        ✕
      </button>
    </div>
  );
};

export default WelcomeBanner;
export const __testing = { STORAGE_KEY };
